<template>
    <settings-popup-base
        :resources="{ 
            deleteButtonText: resource('Button_Remove'),  
            ...resources 
        }"
        :invalid="$v.$invalid || invalid"
        
        @delete="showDeletePrompt = true"
        @save="$emit('save')"
        @hide="$emit('hidden')"
        @display-errors="onDisplayErrors()"
    >
        <template #title>
            <div>
                <div>
                    <h3>{{resources.brickTitle}}</h3>
                </div>

                <div>
                    <navigate-to-actionable-report-link v-if="displayActionableReportNavigation"
                        :journey-id="journey.id"
                        :highlighted-brick-id="brick.id"
                        :disabled="isNew"
                        class="btn btn-outline-secondary btn-xs mr-3"
                    />

                    <copy-id-button :id="brick.id" />
                </div>
            </div>
        </template>

        <template #body>
            <settings-property icon="far fa-edit" :label="resource('JourneyComposer_BrickSettingsBase_Name_DisplayName')">
                <template #input="{id}">
                    <input :id="id" name="name" type="text" class="form-control" :disabled="!canWrite" v-model="nameModel" />
                </template>
                
                <template #errors>
                    <required-error-message v-if="$v.nameModel.$dirty && $v.nameModel.$invalid" :field-name="resource('JourneyComposer_BrickSettingsBase_Name_DisplayName')" />
                </template>
            </settings-property>
            
            <slot name="default" />

            <settings-property v-if="displayNewComment" :label="resource('JourneyComposer_BrickSettingsBase_Comment_Label')">
                <template #input="{id}">
                    <new-comment :brick-id="commentId" :id="id" />
                </template>
            </settings-property>

            <delete-brick-prompt
                v-if="showDeletePrompt"
                :title="resources.deletePromptTitle || resource('JourneyBuilder_GenericSettings_Delete_Title')"
                :body="resources.deletePromptBody || resource('JourneyBuilder_GenericSettings_Delete_Body')"
                :brick-id="brick.id"

                @yes="$emit('delete')"
                @hidden="showDeletePrompt = false"
            />
        </template>
    </settings-popup-base>
</template>

<script>
import {brickDefinitions} from '@modules/JourneyBuilder/Bricks/brick-definitions';
import {required} from 'vuelidate/lib/validators';

import {validationMixin} from 'vuelidate';
import resourceMixin from '@core/Util/Mixins/resources';
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
import injectJourneyMixin from '@modules/JourneyBuilder/Mixins/inject-journey';

import SettingsPopupBase from "./SettingsPopupBase";
import NavigateToActionableReportLink from '@modules/JourneyBuilder/Components/Elements/NavigateToActionableReportLink';
import CopyIdButton from '@modules/JourneyBuilder/Components/Elements/CopyIdButton';
import DeleteBrickPrompt from '@modules/JourneyBuilder/Components/Composer/DeleteBrickPrompt';
import {NewComment} from '@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Comments';
import InputGroup from "@core/Components/Inputs/InputGroup";
import SettingsProperty from './SettingsProperty';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";

export default {
    name: "brick-settings-popup",
    components: {
        RequiredErrorMessage,
        SettingsPopupBase,
        NavigateToActionableReportLink,
        CopyIdButton,
        DeleteBrickPrompt,
        NewComment,
        InputGroup,
        SettingsProperty
    },
    mixins: [permissionsMixin, validationMixin, resourceMixin, injectJourneyMixin],
    
    props: {
        /**
         * @property {String} deletePromptTitle
         * @property {String} deletePromptBody
         * @property {String} deleteButtonText
         * @property {String} brickTitle
         */
        resources: {
            type: Object,
            required: true
        },
        
        /** @type {JourneyBrick} */
        brick: {
            type: Object,
            required: true
        },
        
        name: {
            type: String,
            required: false
        },

        invalid: {
            type: Boolean,
            required: false,
            default: false
        },

        includeActionableReportNavigation: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    
    data(){
        return {
            showDeletePrompt: false
        }
    },
    
    computed: {
        nameModel: {
            get(){
                return this.name;
            },
            set(value){
                this.$v.nameModel.$touch();
                this.$emit('update:name', value);
            }
        },
        
        displayNewComment(){
            //Creating comments requires saving the brick; something users without write permission can't do
            if(!this.canWrite){
                return false;
            }

            //At the very least our brick must be marked as supporting comments
            return brickDefinitions[this.brick.type].supportsComments;
        },
        
        displayActionableReportNavigation(){
            //Navigation needs to be explicitly enabled
            //Brick must not be new (journey is implied to not be new)
            return this.includeActionableReportNavigation && !this.brick.isNew;
        },

        commentId() {
            return "brick:" + this.brick.id;
        },
        
        isNew(){
            return this.brick.isNew;
        }
    },
    
    validations: {
        nameModel: {
            required
        }
    },
    
    methods: {
        onDisplayErrors(){
            this.$v.$touch();
            this.$emit('display-errors');
        }
    }
}
</script>